import { useState, useEffect, useCallback } from "react";
import type { Page } from "@/types/trading";

const PAGE_DEFAULT_TAB: Partial<Record<Page, string>> = {
  home:     "dashboard",
  lab:      "data",
  trade:    "positions",
  paper:    "positions",
  pnl:      "dayview",
  settings: "config",
  // legacy keys — kept for compat while old pages still render
  backtest: "data",
  replay:   "library",
  gtt:      "scan",
};

/** Maps old page keys → new hash paths (used for transparent redirects). */
const OLD_PAGE_REDIRECTS: Partial<Record<string, string>> = {
  // dashboard stays as-is — MCT logo navigates there directly
  "scan-monitor": "#/home/scan",
  subscriptions: "#/home/subscriptions",
  backtest:      "#/replay",
  "day-replay":  "#/replay/chart",
  scheduler:     "#/settings/automation",
  gtt:           "#/trade/gtt",
};

/** All page keys recognised as valid (old keys kept during rollout). */
const VALID_PAGES = new Set<Page>([
  "home", "lab", "trade", "pnl", "paper", "chat", "settings",
  // legacy — still valid until Phase 6 cleanup
  "dashboard", "backtest", "scheduler", "logs",
  "subscriptions", "gtt", "replay", "day-replay", "scan-monitor",
  "strategy-pnl",
]);

function parseHash(hash: string): { page: Page; tab: string | null } {
  // "#/trade/positions" → page="trade", tab="positions"
  // "#/home"            → page="home",  tab="dashboard"
  const raw   = hash.replace(/^#\/?/, "");
  const parts = raw.split("/").filter(Boolean);
  const rawPage = parts[0] ?? "";

  // Redirect old keys → new pages (state only; URL updated in useEffect)
  const redir = OLD_PAGE_REDIRECTS[rawPage];
  if (redir) {
    const redirParts = redir.replace(/^#\//, "").split("/");
    const newPage = redirParts[0] as Page;
    const newTab  = redirParts[1] ?? parts[1] ?? PAGE_DEFAULT_TAB[newPage] ?? null;
    return { page: newPage, tab: newTab };
  }

  const page = (VALID_PAGES.has(rawPage as Page) ? rawPage : "dashboard") as Page;
  const tab  = parts[1] ?? PAGE_DEFAULT_TAB[page] ?? null;
  return { page, tab };
}

function buildHash(page: Page, tab?: string | null): string {
  const t = tab ?? PAGE_DEFAULT_TAB[page] ?? null;
  return t ? `#/${page}/${t}` : `#/${page}`;
}

export function useHashRouter() {
  const [{ page, tab }, setState] = useState(() =>
    parseHash(window.location.hash),
  );

  // On first render: redirect old page keys and set a sensible default hash.
  useEffect(() => {
    const raw     = window.location.hash.replace(/^#\/?/, "");
    const rawPage = raw.split("/")[0] ?? "";
    if (OLD_PAGE_REDIRECTS[rawPage]) {
      window.location.hash = OLD_PAGE_REDIRECTS[rawPage]!;
    } else if (!window.location.hash || window.location.hash === "#") {
      window.location.hash = "#/dashboard";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep state in sync when the user hits Back/Forward
  useEffect(() => {
    const onHashChange = () => setState(parseHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Navigate to a new page (and optional tab).
  const navigate = useCallback((newPage: Page, newTab?: string) => {
    window.location.hash = buildHash(newPage, newTab);
    // hashchange event fires and updates state automatically
  }, []);

  // Change only the tab segment, keep current page
  const setTab = useCallback(
    (newTab: string) => {
      window.location.hash = buildHash(page, newTab);
    },
    [page],
  );

  return { page, tab, navigate, setTab };
}
